import { useEffect, useState, type ReactNode } from "react";
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import { Button } from "./Button";
import { ButtonText } from "./ButtonText";
import {
  fonts,
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

export function AppModal({
  children,
  title,
  visible,
  confirmOnClose = false,
  onClose,
}: {
  children: ReactNode;
  confirmOnClose?: boolean;
  title: string;
  visible: boolean;
  onClose: () => void;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  const [confirmingClose, setConfirmingClose] = useState(false);
  useEffect(() => {
    if (!visible) setConfirmingClose(false);
  }, [visible]);
  const requestClose = () => {
    if (confirmOnClose) {
      setConfirmingClose(true);
      return;
    }
    onClose();
  };
  const close = () => {
    setConfirmingClose(false);
    onClose();
  };

  // Unmount when closed so web a11y trees do not keep ghost form fields.
  if (!visible) return null;

  return (
    <Modal
      animationType="fade"
      transparent
      visible
      onRequestClose={requestClose}
    >
      <Pressable style={stylesheet.overlay} onPress={requestClose}>
        <Pressable
          role="dialog"
          accessibilityViewIsModal
          accessibilityLabel={title}
          style={stylesheet.card}
          onPress={(event) => event.stopPropagation()}
        >
          <View style={stylesheet.header}>
            <Text style={stylesheet.title}>{title}</Text>
            <Button
              variant="text"
              compact
              accessibilityLabel="Close"
              onPress={requestClose}
            >
              <ButtonText>Close</ButtonText>
            </Button>
          </View>
          <ScrollView contentContainerStyle={stylesheet.content}>
            {confirmingClose ? (
              <>
                <Text style={stylesheet.confirmTitle}>Discard changes?</Text>
                <Text style={stylesheet.confirmDescription}>
                  Unsaved changes in this modal will be lost.
                </Text>
                <View style={stylesheet.actions}>
                  <Button
                    variant="outline"
                    compact
                    onPress={() => setConfirmingClose(false)}
                  >
                    <ButtonText>Keep editing</ButtonText>
                  </Button>
                  <Button variant="danger" compact onPress={close}>
                    <ButtonText>Discard</ButtonText>
                  </Button>
                </View>
              </>
            ) : (
              children
            )}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    overlay: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.l,
      backgroundColor: colors.overlay,
    },
    card: {
      width: "100%",
      maxWidth: 640,
      maxHeight: "92%",
      overflow: "hidden",
      borderRadius: radii.l,
      backgroundColor: colors.elevated,
    },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.m,
      padding: spacing.l,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    title: { color: colors.heading, fontFamily: fonts.bold, fontSize: 22 },
    content: { gap: spacing.m, padding: spacing.l },
    confirmTitle: {
      color: colors.heading,
      fontFamily: fonts.bold,
      fontSize: 18,
    },
    confirmDescription: {
      color: colors.foreground,
      fontFamily: fonts.regular,
      fontSize: 14,
    },
    actions: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "flex-end",
      gap: spacing.s,
    },
  });
